<script setup>
import { computed } from 'vue';
import { Carousel, Navigation, Slide } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import SportLiveTvEventCard from '@/sport-live-tv/components/sport-live-tv-event-card.vue';
import { SPORT_LIVE_TV_CAROUSEL_BREAKPOINTS, SPORT_LIVE_TV_CAROUSEL_SETTINGS, SPORT_LIVE_TV_EVENTS_PER_ROW } from '@/sport-live-tv/constants';

const props = defineProps({
  title: { type: String, required: true },
  events: { type: Array, required: true },
  scoresVisible: { type: Boolean, default: false },
  titleClickable: { type: Boolean, default: false },
});

const emit = defineEmits(['select', 'title-click']);

const visibleEvents = computed(() => props.events.slice(0, SPORT_LIVE_TV_EVENTS_PER_ROW));
</script>

<template>
  <section class="sport-live-tv-row">
    <h2 class="sport-live-tv-row__header">
      <button v-if="titleClickable" type="button" class="sport-live-tv-row__title sport-live-tv-row__title--link" @click="emit('title-click')">
        {{ title }}
        <span class="sport-live-tv-row__arrow" aria-hidden="true">›</span>
      </button>
      <span v-else class="sport-live-tv-row__title">{{ title }}</span>
    </h2>
    <Carousel class="sport-live-tv-row__carousel" :settings="SPORT_LIVE_TV_CAROUSEL_SETTINGS" :breakpoints="SPORT_LIVE_TV_CAROUSEL_BREAKPOINTS">
      <Slide v-for="event in visibleEvents" :key="event.id" class="sport-live-tv-row__slide">
        <SportLiveTvEventCard :event="event" :scores-visible="scoresVisible" @select="emit('select', $event)" />
      </Slide>
      <template #addons>
        <Navigation />
      </template>
    </Carousel>
  </section>
</template>

<style lang="scss" scoped>
.sport-live-tv-row {
  &__header {
    margin: 0 0 0.5em;
    line-height: 1.3;
  }

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0;
    border: 0;
    background: none;
    color: $gray500;
    font-family: inherit;
    font-size: 1.4vw;
    font-weight: 700;

    @media (max-width: $mobile) {
      font-size: 15px;
    }

    &--link {
      cursor: pointer;

      &:hover {
        color: $white;
      }
    }
  }

  &__arrow {
    color: $bg-red;
    line-height: 1;
  }

  &__carousel.carousel {
    margin-bottom: 0;

    :deep(.carousel__viewport) {
      margin: 0 -12px;
    }

    :deep(.carousel__track) {
      padding: 16px 0 24px;
    }

    :deep(.carousel__prev),
    :deep(.carousel__next) {
      top: 50%;
      width: 40px;
      height: 40px;
      margin-top: 0;
      border-radius: 50%;
      background-color: rgba($black, 0.6);
      opacity: 0;
      transition: opacity 0.2s ease-in-out;
    }

    :deep(.carousel__prev--in-active),
    :deep(.carousel__next--in-active) {
      display: none;
    }

    &:hover :deep(.carousel__prev),
    &:hover :deep(.carousel__next) {
      opacity: 1;
    }
  }

  &__slide {
    align-items: stretch;
    padding: 0 12px;
  }
}
</style>
